// D97: the stated (IFS list) selling price of a quotation line - the rules about it the page and the
// price-application owner share, kept pure so each has one owner. See `priceLineAt({statedSellingPrice})`
// for the lock itself (the mirrored pricing twins keep their own positive-number check on purpose:
// they are the owner of the arithmetic and import nothing from a feature folder).
import { unitChanged } from '../../utils/unitVocabulary';
import { LOCKED_UNIT_CODE } from '../../utils/pricing';

/** Typing a selling price, a total or a markup is the explicit override: the pin is cleared. */
const UNLOCKING_FIELDS = new Set(['unitSellingPrice', 'totalSellingPrice', 'markup']);

export const statedSellingAfterEdit = (
  field: string,
  current: { statedSellingPrice?: number | null; statedSellingUom?: string | null }
): { statedSellingPrice: number | null; statedSellingUom: string | null } =>
  UNLOCKING_FIELDS.has(field)
    ? { statedSellingPrice: null, statedSellingUom: null }
    : { statedSellingPrice: current.statedSellingPrice ?? null, statedSellingUom: current.statedSellingUom ?? null };

/** Is this line locked at a stated selling price? The ONE reading of the field. */
export const isLockedAtStatedPrice = (
  line: { statedSellingPrice?: number | null } | null | undefined
): boolean => Number(line?.statedSellingPrice) > 0;

/**
 * A pick, refresh or catalogue addition that would put a locked line in another unit is refused,
 * never applied: the price is stated per a unit, and letting it go would silently drop the line to
 * the house rate. The message for the user, or null when the line is not locked or the unit is
 * the same unit as the stated price's (`unitChanged`, the pricing owner's test: KGS = KG, blank = blank).
 */
export const lockedUnitRefusal = (
  item: { statedSellingUom?: string | null; statedSellingPrice?: number | null },
  targetUom: string | null | undefined
): string | null => {
  if (!isLockedAtStatedPrice(item) || !unitChanged(item.statedSellingUom, targetUom)) return null;
  return `This line is locked at the IFS list price per ${item.statedSellingUom || '?'}. Type a selling price or markup to unlock it before changing its unit.`;
};

/** Thrown by the price-application owner so no caller can move a locked line to another unit. */
export class LockedLineError extends Error {
  readonly code = LOCKED_UNIT_CODE;
  constructor(message: string) {
    super(message);
    this.name = 'LockedLineError';
  }
}

/** The ONE sentence for the locked-line refusal wherever it reaches the user from a code (server 409 or client throw). */
export const LOCKED_LINE_UNLOCK_MESSAGE =
  'A line is locked at the IFS list price in another unit. Type a selling price or markup on it to unlock it.';

/** Is this the locked-line refusal - from the owner or from the price-application owner? Nothing else. */
export const isLockedLineError = (e: unknown): e is Error => (e as { code?: unknown } | null)?.code === LOCKED_UNIT_CODE;
